import { useState } from "react";
import { motion } from "framer-motion";
import { Building2, TrendingUp } from "lucide-react";

// Product-based companies only (no IT-services / consulting firms).
// Four rows — a company in one row never repeats in another.
const ROWS = [
  [
    { name: "Google", logo: "https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" },
    { name: "Microsoft", logo: "https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg" },
    { name: "Amazon", logo: "https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg" },
    { name: "Meta", logo: "https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg" },
    { name: "Apple", logo: "https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg" },
    { name: "Netflix", logo: "https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg" },
    { name: "Airbnb", logo: "https://upload.wikimedia.org/wikipedia/commons/6/69/Airbnb_Logo_B%C3%A9lo.svg" },
    { name: "LinkedIn", logo: "https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png" },
    { name: "Slack", logo: "https://upload.wikimedia.org/wikipedia/commons/d/d5/Slack_icon_2019.svg" },
    { name: "Dropbox", logo: "https://upload.wikimedia.org/wikipedia/commons/7/78/Dropbox_Icon.svg" },
  ],
  [
    { name: "NVIDIA", logo: "https://upload.wikimedia.org/wikipedia/commons/2/21/Nvidia_logo.svg" },
    { name: "Adobe", logo: "https://upload.wikimedia.org/wikipedia/commons/7/7b/Adobe_Systems_logo_and_wordmark.svg" },
    { name: "Salesforce", logo: "https://upload.wikimedia.org/wikipedia/commons/f/f9/Salesforce.com_logo.svg" },
    { name: "Oracle", logo: "https://upload.wikimedia.org/wikipedia/commons/5/50/Oracle_logo.svg" },
    { name: "IBM", logo: "https://upload.wikimedia.org/wikipedia/commons/5/51/IBM_logo.svg" },
    { name: "Intel", logo: "https://upload.wikimedia.org/wikipedia/commons/7/7d/Intel_logo_%282006-2020%29.svg" },
    { name: "Shopify", logo: "https://upload.wikimedia.org/wikipedia/commons/0/0e/Shopify_logo_2018.svg" },
    { name: "Zoom", logo: "https://upload.wikimedia.org/wikipedia/commons/7/7b/Zoom_Communications_Logo.svg" },
    { name: "Pinterest", logo: "https://upload.wikimedia.org/wikipedia/commons/0/08/Pinterest-logo.png" },
    { name: "Reddit", logo: "https://upload.wikimedia.org/wikipedia/en/b/bd/Reddit_Logo_Icon.svg" },
  ],
  [
    { name: "Cisco", logo: "https://upload.wikimedia.org/wikipedia/commons/0/08/Cisco_logo_blue_2016.svg" },
    { name: "SAP", logo: "https://upload.wikimedia.org/wikipedia/commons/5/59/SAP_2011_logo.svg" },
    { name: "Uber", logo: "https://upload.wikimedia.org/wikipedia/commons/5/58/Uber_logo_2018.svg" },
    { name: "PayPal", logo: "https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg" },
    { name: "Stripe", logo: "https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" },
    { name: "Spotify", logo: "https://upload.wikimedia.org/wikipedia/commons/8/84/Spotify_icon.svg" },
    { name: "Snap", logo: "https://upload.wikimedia.org/wikipedia/en/c/c4/Snapchat_logo.svg" },
    { name: "Twilio", logo: "https://commons.wikimedia.org/wiki/Special:FilePath/Twilio-logo-red.svg" },
    { name: "MongoDB", logo: "https://upload.wikimedia.org/wikipedia/commons/9/93/MongoDB_Logo.svg" },
    { name: "Figma", logo: "https://upload.wikimedia.org/wikipedia/commons/3/33/Figma-logo.svg" },
  ],
  [
    { name: "Qualcomm", logo: "https://commons.wikimedia.org/wiki/Special:FilePath/Qualcomm-Logo.svg" },
    { name: "AMD", logo: "https://upload.wikimedia.org/wikipedia/commons/7/7c/AMD_Logo.svg" },
    { name: "Atlassian", logo: "https://commons.wikimedia.org/wiki/Special:FilePath/Atlassian-logo.svg" },
    { name: "Samsung", logo: "https://upload.wikimedia.org/wikipedia/commons/b/b4/Samsung_wordmark.svg" },
    { name: "Tesla", logo: "https://upload.wikimedia.org/wikipedia/commons/b/bd/Tesla_Motors.svg" },
    { name: "Snowflake", logo: "https://upload.wikimedia.org/wikipedia/commons/f/ff/Snowflake_Logo.svg" },
    { name: "Cloudflare", logo: "https://commons.wikimedia.org/wiki/Special:FilePath/Cloudflare_Logo.svg" },
    { name: "Okta", logo: "https://commons.wikimedia.org/wiki/Special:FilePath/Okta_logo_%282023%29.svg" },
    { name: "Datadog", logo: "https://upload.wikimedia.org/wikipedia/en/7/7e/Datadog_logo.svg" },
    { name: "Databricks", logo: "https://upload.wikimedia.org/wikipedia/commons/6/63/Databricks_Logo.png" },
  ],
];

function LogoCard({ company }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="flex items-center justify-center shrink-0 h-20 w-44 rounded-2xl bg-white ring-1 ring-gray-100 shadow-sm px-6 hover:ring-aiblue/40 hover:shadow-md transition">
      {failed ? (
        <span className="font-display font-bold text-gray-700 text-base">
          {company.name}
        </span>
      ) : (
        <img
          src={company.logo}
          alt={company.name}
          loading="lazy"
          onError={() => setFailed(true)}
          className="max-h-9 w-auto object-contain"
          style={{ maxWidth: "120px" }}
        />
      )}
    </div>
  );
}

function Marquee({ items, reverse = false }) {
  return (
    <div className="relative overflow-hidden">
      <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#FFF6F0] to-transparent z-10" />
      <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#FFF6F0] to-transparent z-10" />
      <div
        className={`flex gap-4 w-max hp-marquee ${
          reverse ? "hp-marquee-reverse" : ""
        }`}
      >
        {[...items, ...items].map((c, i) => (
          <LogoCard key={`${c.name}-${i}`} company={c} />
        ))}
      </div>
    </div>
  );
}

export default function SmruHiring() {
  return (
    <section
      id="hiring"
      className="py-20 sm:py-28 bg-gradient-to-b from-[#FFF6F0] via-white to-[#FFF6F0]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-aiorange/10 text-aiorange">
            <Building2 className="h-3.5 w-3.5" />
            Hiring Companies
          </span>
          <h2 className="mt-5 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-gray-900">
            Where Our Students{" "}
            <span className="bg-gradient-to-r from-[#0052FF] to-[#FF6B00] bg-clip-text text-transparent">
              Get Hired
            </span>
          </h2>
          <p className="mt-4 text-gray-600 text-base sm:text-lg">
            From high-growth startups to global product giants a network of{" "}
            <span className="font-bold text-gray-900">1800+ companies</span>{" "}
            actively hiring AI-ready talent.
          </p>
        </motion.div>

        {/* Logo marquees — 4 rows */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-12 space-y-4"
        >
          {ROWS.map((row, i) => (
            <Marquee key={i} items={row} reverse={i % 2 === 1} />
          ))}
        </motion.div>

        {/* Footnote */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-10 flex items-center justify-center gap-2 text-sm text-gray-500"
        >
          <TrendingUp className="h-4 w-4 text-aiorange" />
          <span>
            …and <span className="font-bold text-gray-900">1800+ more</span>{" "}
            product companies hiring across AI, data, cloud &amp; security.
          </span>
        </motion.div>
      </div>
    </section>
  );
}
